import { Upload, Download, FileUp } from "lucide-react";
import { useCsvImport } from "../hooks/useCsvImport";
import { parseCsvRows, downloadCsvTemplate } from "../lib/csvImport";
import { CustomerImportPreview } from "./ImportPreview";
import { FormDialog, Field, ActionButton } from "./common";

const HEADER_MAP = {
  name: "name",
  company: "name",
  company_name: "name",
  customer: "name",
  contact: "contact_name",
  contact_name: "contact_name",
  contact_person: "contact_name",
  requestor: "requestor",
  requester: "requestor",
  requested_by: "requestor",
  email: "email",
  phone: "phone",
  address: "address",
  notes: "notes",
  note: "notes",
};

const TEMPLATE = "name,contact_name,requestor,email,phone,address,notes\nAcme Energy Partners,Dan Whitfield,Sarah Conley,ops@acme.example,432-555-0184,1200 W County Rd Midland TX,Night gate code 2214\n";

const parseCustomers = (raw) => parseCsvRows(raw, HEADER_MAP, "name");

export const CustomerImport = ({ open, onOpenChange, onDone }) => {
  const { rows, parseErrors, csvText, busy, fileRef, reset, onFile, onPaste, submit } = useCsvImport({
    parse: parseCustomers, endpoint: "/customers/import", entity: "customers", onOpenChange, onDone,
  });
  const downloadTemplate = () => downloadCsvTemplate(TEMPLATE, "customers_import_template.csv");

  return (
    <FormDialog open={open} onOpenChange={(v) => { onOpenChange(v); if (!v) reset(); }} title="Import Customers" testId="customer-import-dialog">
      <div className="flex items-center justify-between">
        <p className="text-xs text-slate-400 leading-relaxed max-w-[300px]">
          CSV columns: <span className="font-mono text-amber-400/90">name, contact_name, requestor, email, phone, address, notes</span>
        </p>
        <button type="button" data-testid="import-template-download" onClick={downloadTemplate} className="inline-flex items-center gap-1.5 text-xs font-mono uppercase tracking-wider text-sky-400 hover:text-sky-300 transition-colors duration-200">
          <Download size={13} /> Template
        </button>
      </div>

      <Field label="Upload CSV file">
        <label
          data-testid="import-file-drop"
          className="flex items-center justify-center gap-2 border border-dashed border-slate-600 rounded-md px-4 py-5 text-sm text-slate-400 hover:border-amber-500/50 hover:text-amber-300 cursor-pointer transition-colors duration-200"
        >
          <FileUp size={16} /> Choose .csv file
          <input ref={fileRef} data-testid="import-file-input" type="file" accept=".csv,text/csv" onChange={onFile} className="hidden" />
        </label>
      </Field>

      <Field label="…or paste CSV text">
        <textarea
          data-testid="import-csv-textarea"
          value={csvText}
          onChange={(e) => onPaste(e.target.value)}
          rows={5}
          placeholder={"name,contact_name,requestor,email,phone,address,notes\nAcme Energy,Dan,Sarah,ops@acme.example,432-555-0184,Midland TX,"}
          className="w-full bg-slate-900/80 border border-slate-700 rounded-md px-3 py-2 text-xs font-mono text-slate-100 placeholder:text-slate-600 focus:outline-none focus:border-amber-500/60 focus:ring-1 focus:ring-amber-500/40 transition-colors duration-200"
        />
      </Field>

      {parseErrors.length > 0 && (
        <div className="bg-red-500/5 border border-red-500/30 rounded-md p-3 space-y-1" data-testid="import-parse-errors">
          {parseErrors.slice(0, 5).map((e) => (
            <p key={e} className="text-xs text-red-400 font-mono">{e}</p>
          ))}
        </div>
      )}

      <CustomerImportPreview rows={rows} />

      <ActionButton testId="import-submit-button" onClick={submit} disabled={busy || rows.length === 0} className="w-full justify-center">
        <Upload size={16} /> {busy ? "Importing…" : `Import ${rows.length > 0 ? rows.length : ""} Customer${rows.length === 1 ? "" : "s"}`}
      </ActionButton>
    </FormDialog>
  );
};
